import React, { useState, useEffect } from 'react';
import { userSettingsAPI } from '../../services/api';
import api from '../../services/api';
import './SettingsPage.css';

function SecurityDetails() {
  const [message, setMessage] = useState({ type: '', text: '' });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Phone verification state
  const [phoneStatus, setPhoneStatus] = useState({ hasVerifiedPhone: false, phone: null });
  const [phoneStep, setPhoneStep] = useState('idle'); // idle, sending, verify
  const [otpCode, setOtpCode] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneSentTo, setPhoneSentTo] = useState('');

  useEffect(() => {
    api.get('/api/otp/status').then(res => {
      setPhoneStatus(res.data);
    }).catch(() => {});
  }, []);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }
    try {
      setChangingPassword(true);
      setMessage({ type: '', text: '' });
      await userSettingsAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to change password' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSendOtp = async () => {
    setPhoneLoading(true);
    try {
      const res = await api.post('/api/otp/send');
      if (res.data.status === 'success') {
        setPhoneStep('verify');
        setPhoneSentTo(res.data.sent_to || '');
        setMessage({ type: 'success', text: `Verification code sent to ${res.data.sent_to}` });
      } else {
        setMessage({ type: 'error', text: res.data.message || 'Failed to send code' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to send code' });
    }
    setPhoneLoading(false);
  };

  const handleVerifyOtp = async () => {
    setPhoneLoading(true);
    try {
      const res = await api.post('/api/otp/verify', { code: otpCode });
      if (res.data.status === 'success') {
        setPhoneStatus({ ...phoneStatus, hasVerifiedPhone: true });
        setPhoneStep('idle');
        setOtpCode('');
        setMessage({ type: 'success', text: 'Phone number verified successfully!' });
      } else {
        setMessage({ type: 'error', text: res.data.message || 'Invalid code' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Verification failed' });
    }
    setPhoneLoading(false);
  };

  return (
    <div className="settings-page-content">
      <h2>Security Details</h2>
      <p className="page-description">Manage your password and security settings.</p>

      {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

      <form onSubmit={handlePasswordSubmit} className="settings-form">
        <div className="form-section">
          <h3>Change Password</h3>
          <div className="form-group full-width">
            <label htmlFor="currentPassword">Current Password *</label>
            <input type="password" id="currentPassword" name="currentPassword"
              value={passwordForm.currentPassword} onChange={handlePasswordChange} required />
          </div>
          <div className="form-row" style={{ alignItems: 'start' }}>
            <div className="form-group">
              <label htmlFor="newPassword">New Password *</label>
              <input type="password" id="newPassword" name="newPassword"
                value={passwordForm.newPassword} onChange={handlePasswordChange} required minLength="8" />
              <small>Minimum 8 characters</small>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password *</label>
              <input type="password" id="confirmPassword" name="confirmPassword"
                value={passwordForm.confirmPassword} onChange={handlePasswordChange} required minLength="8" />
              <small>&nbsp;</small>
            </div>
          </div>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={changingPassword}>
            {changingPassword ? 'Changing Password...' : 'Change Password'}
          </button>
        </div>
      </form>

      <div className="form-section" style={{ marginTop: 30 }}>
        <h3>Phone Verification</h3>
        {phoneStatus.hasVerifiedPhone ? (
          <div className="info-box" style={{ borderColor: '#065f46' }}>
            <p style={{ color: '#10b981', fontWeight: 600, margin: 0 }}>
              Phone verified: {phoneStatus.phone}
            </p>
            <p style={{ color: '#aaa', fontSize: 13, margin: '8px 0 0' }}>
              Your phone is verified. OTP will be required when signing in for added security.
            </p>
          </div>
        ) : (
          <div>
            <p style={{ color: '#aaa', marginBottom: 16 }}>
              Verify your phone number to enable two-factor authentication via SMS.
              {phoneStatus.phone && <> Your phone on file: <strong style={{ color: '#fff' }}>{phoneStatus.phone}</strong></>}
            </p>

            {phoneStep === 'idle' && (
              <button onClick={handleSendOtp} disabled={phoneLoading} className="btn btn-primary">
                {phoneLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            )}

            {phoneStep === 'verify' && (
              <div>
                <p style={{ color: '#aaa', marginBottom: 12 }}>
                  Enter the 6-digit code sent to {phoneSentTo}
                </p>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <input
                    type="text" value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" maxLength={6}
                    style={{
                      textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.4em',
                      width: 200, padding: '8px 12px', background: '#2a2d3a',
                      border: '1px solid #3a3d4a', borderRadius: 6, color: '#fff'
                    }}
                  />
                  <button onClick={handleVerifyOtp} disabled={phoneLoading || otpCode.length < 6} className="btn btn-primary">
                    {phoneLoading ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
                <div style={{ marginTop: 12 }}>
                  <button onClick={handleSendOtp} disabled={phoneLoading}
                    style={{ background: 'none', border: 'none', color: '#fbbf24', cursor: 'pointer', fontSize: 13 }}>
                    Resend Code
                  </button>
                  <span style={{ color: '#444', margin: '0 8px' }}>|</span>
                  <button onClick={() => { setPhoneStep('idle'); setOtpCode(''); }}
                    style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 13 }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SecurityDetails;
